import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import supplyContinuousDocumentService from '../../services/supplyContinuousDocumentService';
import SupplierSelector from '../../components/PurchaseOrders/SupplierSelector';
import CreateSupplierModal from '../../components/PurchaseOrders/CreateSupplierModal';
import { supplierService } from '../../services/supplierService';
import Toast from '../../components/Common/Toast';
import { DOCUMENT_TYPE_OPTIONS } from '../../utils/documentTypes';

const CreateSupplyContinuousDocument = () => {
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    supplier_id: '',
    document_type: 'factura', // 'factura', 'boleta', 'nota_credito', 'nota_debito', 'otro'
    series: '',
    number: '',
    issue_date: new Date().toISOString().split('T')[0],
    due_date: '',
    currency: 'PEN',
    base_imponible: '',
    igv_type: '18', // '18', '10', 'exempt'
    igv_amount: '',
    no_gravados: '',
    total_amount: '',
    payment_condition_type: 'cash', // 'cash', 'days', 'date'
    payment_condition_days: 30,
    payment_condition_date: '',
  });

  const [suppliers, setSuppliers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [toast, setToast] = useState({ isOpen: false, message: '', type: 'info' });
  const [errors, setErrors] = useState({});
  const [isCreateSupplierModalOpen, setIsCreateSupplierModalOpen] = useState(false);
  const hasLoadedFormData = useRef(false);

  useEffect(() => {
    if (!hasLoadedFormData.current) {
      hasLoadedFormData.current = true;
      loadFormData();
    }
  }, []);

  // Calcular IGV y total automáticamente
  useEffect(() => {
    const base = parseFloat(formData.base_imponible || 0);
    let igv = 0;
    
    // Calcular IGV según el tipo seleccionado
    if (formData.igv_type === '18' && base > 0) {
      igv = base * 0.18;
    } else if (formData.igv_type === '10' && base > 0) {
      igv = base * 0.10;
    } else if (formData.igv_type === '10.5' && base > 0) {
      igv = base * 0.105;
    } else if (formData.igv_type === 'exempt') {
      igv = 0;
    }
    
    const noGrav = parseFloat(formData.no_gravados || 0);
    const total = base + igv + noGrav;
    
    // Actualizar IGV si cambió
    if (Math.abs(parseFloat(formData.igv_amount || 0) - igv) > 0.01) {
      setFormData(prev => ({ ...prev, igv_amount: igv.toFixed(2) }));
    }
    
    // Actualizar total si cambió
    if (!formData.total_amount || Math.abs(parseFloat(formData.total_amount || 0) - total) > 0.01) {
      setFormData(prev => ({ ...prev, total_amount: total.toFixed(2) }));
    }
  }, [formData.base_imponible, formData.igv_type, formData.no_gravados]);

  const loadFormData = async () => {
    try {
      const suppliersRes = await supplyContinuousDocumentService.getSuppliers();
      if (suppliersRes.success) {
        setSuppliers(suppliersRes.data || []);
      }
    } catch (error) {
      console.error('Error al cargar datos del formulario:', error);
      setToast({
        isOpen: true,
        message: 'Error al cargar los datos del formulario',
        type: 'error'
      });
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: null }));
    }
  };

  // Calcular fecha de pago basándose en la condición seleccionada
  const calculatePaymentDate = () => {
    if (!formData.issue_date) return null;
    
    const issueDate = new Date(formData.issue_date);
    
    if (formData.payment_condition_type === 'cash') {
      return issueDate;
    } else if (formData.payment_condition_type === 'days') {
      const days = parseInt(formData.payment_condition_days) || 0;
      const paymentDate = new Date(issueDate);
      paymentDate.setDate(paymentDate.getDate() + days);
      return paymentDate;
    } else if (formData.payment_condition_type === 'date' && formData.payment_condition_date) {
      return new Date(formData.payment_condition_date);
    }
    
    return null;
  };

  // Calcular días hasta la fecha de pago
  const calculateDaysUntilPayment = () => {
    const paymentDate = calculatePaymentDate();
    if (!paymentDate || !formData.issue_date) return null;
    
    const issueDate = new Date(formData.issue_date);
    const diffTime = paymentDate - issueDate;
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    
    return diffDays;
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData.supplier_id) {
      newErrors.supplier_id = 'El proveedor es requerido';
    }
    if (!formData.issue_date) {
      newErrors.issue_date = 'La fecha de emisión es requerida';
    }
    if (!formData.base_imponible || parseFloat(formData.base_imponible) <= 0) {
      newErrors.base_imponible = 'La base imponible debe ser mayor a 0';
    }
    if (!formData.igv_amount || parseFloat(formData.igv_amount) < 0) {
      newErrors.igv_amount = 'El IGV no puede ser negativo';
    }
    if (!formData.total_amount || parseFloat(formData.total_amount) <= 0) {
      newErrors.total_amount = 'El monto total debe ser mayor a 0';
    }
    if (formData.payment_condition_type === 'days' && (!formData.payment_condition_days || parseInt(formData.payment_condition_days) <= 0)) {
      newErrors.payment_condition_days = 'Los días deben ser mayor a 0';
    }
    if (formData.payment_condition_type === 'date' && !formData.payment_condition_date) {
      newErrors.payment_condition_date = 'La fecha de pago es requerida';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) {
      setToast({
        isOpen: true,
        message: 'Por favor, completa todos los campos requeridos',
        type: 'error'
      });
      return;
    }

    setLoading(true);
    try {
      const submitData = {
        supplier_id: parseInt(formData.supplier_id),
        document_type: formData.document_type,
        series: formData.series || null,
        number: formData.number || null,
        issue_date: formData.issue_date,
        due_date: formData.due_date || null,
        currency: formData.currency,
        base_imponible: parseFloat(formData.base_imponible),
        igv_amount: parseFloat(formData.igv_amount),
        no_gravados: parseFloat(formData.no_gravados || 0),
        total_amount: parseFloat(formData.total_amount),
        payment_condition_type: formData.payment_condition_type,
        payment_condition_days: formData.payment_condition_type === 'days' ? parseInt(formData.payment_condition_days) : null,
        payment_condition_date: formData.payment_condition_type === 'date' ? formData.payment_condition_date : null,
      };

      const response = await supplyContinuousDocumentService.createDocument(submitData);

      if (response.success) {
        setToast({
          isOpen: true,
          message: response.message || 'Documento creado exitosamente',
          type: 'success'
        });

        // Redirigir a la página de detalle (similar a servicios continuos)
        setTimeout(() => {
          navigate(`/supply-continuous-documents/${response.data.id}`);
        }, 1500);
      } else {
        setToast({
          isOpen: true,
          message: response.message || 'Error al crear el documento',
          type: 'error'
        });
      }
    } catch (error) {
      console.error('Error al crear documento:', error);
      setToast({
        isOpen: true,
        message: 'Error al crear el documento',
        type: 'error'
      });
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (amount) => {
    return `S/ ${parseFloat(amount || 0).toLocaleString('es-PE', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  };

  return (
    <div className="min-h-screen bg-gray-50 p-6">
      <div className="max-w-4xl mx-auto">
        {/* Header */}
        <div className="mb-6">
          <h1 className="text-3xl font-bold text-gray-800">Nuevo Documento por Suministro Continuo</h1>
          <p className="text-gray-600 mt-1">Complete la información básica y los montos totales del documento</p>
        </div>

        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Información Básica */}
          <div className="bg-white rounded-lg shadow-md p-6">
            <h2 className="text-xl font-semibold text-gray-800 mb-4">Información Básica</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="md:col-span-2">
                <div className="flex items-center justify-between mb-2">
                  <label htmlFor="supplier_id" className="block text-sm font-medium text-gray-700">
                    Proveedor <span className="text-red-500">*</span>
                  </label>
                  <button
                    type="button"
                    onClick={() => setIsCreateSupplierModalOpen(true)}
                    className="text-xs px-3 py-1 bg-teal-600 text-white rounded-md hover:bg-teal-700 transition-colors"
                  >
                    + Nuevo
                  </button>
                </div>
                <SupplierSelector
                  value={formData.supplier_id}
                  onChange={(value) => handleChange({ target: { name: 'supplier_id', value } })}
                  suppliers={suppliers}
                  error={errors.supplier_id}
                />
                {errors.supplier_id && (
                  <p className="mt-1 text-sm text-red-600">{errors.supplier_id}</p>
                )}
              </div>

              {/* Tipo de Documento, Serie y Número en una sola línea */}
              <div className="md:col-span-2 grid grid-cols-1 md:grid-cols-3 gap-4">
                <div>
                  <label htmlFor="document_type" className="block text-sm font-medium text-gray-700 mb-2">
                    Tipo de Documento <span className="text-red-500">*</span>
                  </label>
                  <select
                    id="document_type"
                    name="document_type"
                    value={formData.document_type}
                    onChange={handleChange}
                    className="w-full px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-teal-500"
                  >
                    {DOCUMENT_TYPE_OPTIONS.map((opt) => (
                      <option key={opt.value} value={opt.value}>{opt.label}</option>
                    ))}
                  </select>
                </div>

                <div>
                  <label htmlFor="series" className="block text-sm font-medium text-gray-700 mb-2">
                    Serie
                  </label>
                  <input
                    type="text"
                    id="series"
                    name="series"
                    value={formData.series}
                    onChange={handleChange}
                    className="w-full px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-teal-500"
                    placeholder="Ej: F001"
                  />
                </div>

                <div>
                  <label htmlFor="number" className="block text-sm font-medium text-gray-700 mb-2">
                    Número
                  </label>
                  <input
                    type="text"
                    id="number"
                    name="number"
                    value={formData.number}
                    onChange={handleChange}
                    className="w-full px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-teal-500"
                    placeholder="Ej: 000001"
                  />
                </div>
              </div>

              <div>
                <label htmlFor="issue_date" className="block text-sm font-medium text-gray-700 mb-2">
                  Fecha de Emisión <span className="text-red-500">*</span>
                </label>
                <input
                  type="date"
                  id="issue_date"
                  name="issue_date"
                  value={formData.issue_date}
                  onChange={handleChange}
                  className="w-full px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-teal-500"
                />
                {errors.issue_date && (
                  <p className="mt-1 text-sm text-red-600">{errors.issue_date}</p>
                )}
              </div>

              <div>
                <label htmlFor="due_date" className="block text-sm font-medium text-gray-700 mb-2">
                  Fecha de Vencimiento
                </label>
                <input
                  type="date"
                  id="due_date"
                  name="due_date"
                  value={formData.due_date}
                  onChange={handleChange}
                  className="w-full px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-teal-500"
                />
              </div>

              <div>
                <label htmlFor="currency" className="block text-sm font-medium text-gray-700 mb-2">
                  Moneda
                </label>
                <select
                  id="currency"
                  name="currency"
                  value={formData.currency}
                  onChange={handleChange}
                  className="w-full px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-teal-500"
                >
                  <option value="PEN">PEN - Sol Peruano</option>
                  <option value="USD">USD - Dólar Americano</option>
                </select>
              </div>

              <div>
                <label htmlFor="igv_type" className="block text-sm font-medium text-gray-700 mb-2">
                  Tipo de IGV <span className="text-red-500">*</span>
                </label>
                <select
                  id="igv_type"
                  name="igv_type"
                  value={formData.igv_type}
                  onChange={handleChange}
                  className="w-full px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-teal-500"
                >
                  <option value="18">IGV 18%</option>
                  <option value="10.5">IGV 10.5%</option>
                  <option value="10">IGV 10%</option>
                  <option value="exempt">No Gravado</option>
                </select>
              </div>
            </div>
          </div>

          {/* Montos Totales del Documento */}
          <div className="bg-white rounded-lg shadow-md p-6">
            <h2 className="text-xl font-semibold text-gray-800 mb-4">Montos Totales del Documento</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
              <div>
                <label htmlFor="base_imponible" className="block text-sm font-medium text-gray-700 mb-2">
                  Base Imponible <span className="text-red-500">*</span>
                </label>
                <input
                  type="number"
                  step="0.01"
                  id="base_imponible"
                  name="base_imponible"
                  value={formData.base_imponible}
                  onChange={handleChange}
                  className="w-full px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-teal-500"
                  placeholder="0.00"
                />
                {errors.base_imponible && (
                  <p className="mt-1 text-sm text-red-600">{errors.base_imponible}</p>
                )}
              </div>

              <div>
                <label htmlFor="igv_amount" className="block text-sm font-medium text-gray-700 mb-2">
                  IGV <span className="text-red-500">*</span>
                </label>
                <input
                  type="number"
                  step="0.01"
                  id="igv_amount"
                  name="igv_amount"
                  value={formData.igv_amount}
                  readOnly
                  className="w-full px-4 py-2 border border-gray-300 rounded-md bg-gray-50 focus:outline-none focus:ring-2 focus:ring-teal-500"
                  placeholder="0.00"
                />
                {errors.igv_amount && (
                  <p className="mt-1 text-sm text-red-600">{errors.igv_amount}</p>
                )}
              </div>

              <div>
                <label htmlFor="no_gravados" className="block text-sm font-medium text-gray-700 mb-2">
                  No Gravados
                </label>
                <input
                  type="number"
                  step="0.01"
                  id="no_gravados"
                  name="no_gravados"
                  value={formData.no_gravados}
                  onChange={handleChange}
                  className="w-full px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-teal-500"
                  placeholder="0.00"
                />
              </div>

              <div>
                <label htmlFor="total_amount" className="block text-sm font-medium text-gray-700 mb-2">
                  Total <span className="text-red-500">*</span>
                </label>
                <input
                  type="number"
                  step="0.01"
                  id="total_amount"
                  name="total_amount"
                  value={formData.total_amount}
                  readOnly
                  className="w-full px-4 py-2 border border-gray-300 rounded-md bg-gray-50 font-semibold"
                />
                {errors.total_amount && (
                  <p className="mt-1 text-sm text-red-600">{errors.total_amount}</p>
                )}
              </div>
            </div>
          </div>

          {/* Información de Pago */}
          <div className="bg-white rounded-lg shadow-md p-6">
            <h2 className="text-xl font-semibold text-gray-800 mb-4">Condición de Pago</h2>
            <div className="space-y-4">
              {/* Tipo de condición */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-3">
                  Tipo de Condición
                </label>
                <div className="flex flex-wrap gap-4">
                  <label className="flex items-center">
                    <input
                      type="radio"
                      name="payment_condition_type"
                      value="cash"
                      checked={formData.payment_condition_type === 'cash'}
                      onChange={handleChange}
                      className="mr-2 text-teal-600 focus:ring-teal-500"
                    />
                    <span className="text-sm text-gray-700">Al contado</span>
                  </label>
                  <label className="flex items-center">
                    <input
                      type="radio"
                      name="payment_condition_type"
                      value="days"
                      checked={formData.payment_condition_type === 'days'}
                      onChange={handleChange}
                      className="mr-2 text-teal-600 focus:ring-teal-500"
                    />
                    <span className="text-sm text-gray-700">Cada X días</span>
                  </label>
                  <label className="flex items-center">
                    <input
                      type="radio"
                      name="payment_condition_type"
                      value="date"
                      checked={formData.payment_condition_type === 'date'}
                      onChange={handleChange}
                      className="mr-2 text-teal-600 focus:ring-teal-500"
                    />
                    <span className="text-sm text-gray-700">Fecha específica</span>
                  </label>
                </div>
              </div>

              {/* Días (si es tipo 'days') */}
              {formData.payment_condition_type === 'days' && (
                <div>
                  <label htmlFor="payment_condition_days" className="block text-sm font-medium text-gray-700 mb-2">
                    Días para pagar <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="number"
                    id="payment_condition_days"
                    name="payment_condition_days"
                    value={formData.payment_condition_days}
                    onChange={(e) => {
                      const value = parseInt(e.target.value) || 0;
                      const validDays = Math.max(0, Math.min(90, value)); // Máximo 90 días
                      handleChange({ target: { name: 'payment_condition_days', value: validDays.toString() } });
                    }}
                    min="1"
                    max="90"
                    className="w-full px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-teal-500"
                  />
                  {errors.payment_condition_days && (
                    <p className="mt-1 text-sm text-red-600">{errors.payment_condition_days}</p>
                  )}
                </div>
              )}

              {/* Fecha específica (si es tipo 'date') */}
              {formData.payment_condition_type === 'date' && (
                <div>
                  <label htmlFor="payment_condition_date" className="block text-sm font-medium text-gray-700 mb-2">
                    Fecha de Pago <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="date"
                    id="payment_condition_date"
                    name="payment_condition_date"
                    value={formData.payment_condition_date}
                    onChange={handleChange}
                    min={formData.issue_date}
                    className="w-full px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-teal-500"
                  />
                  {errors.payment_condition_date && (
                    <p className="mt-1 text-sm text-red-600">{errors.payment_condition_date}</p>
                  )}
                </div>
              )}

              {/* Información calculada */}
              {calculatePaymentDate() && (
                <div className="p-4 bg-blue-50 rounded-lg border border-blue-200">
                  <div className="text-sm text-gray-600 mb-1">Fecha de Pago Calculada</div>
                  <div className="text-lg font-semibold text-blue-700">
                    {calculatePaymentDate().toLocaleDateString('es-PE', { 
                      weekday: 'long', 
                      year: 'numeric', 
                      month: 'long', 
                      day: 'numeric' 
                    })}
                  </div>
                  {formData.payment_condition_type !== 'cash' && calculateDaysUntilPayment() !== null && (
                    <div className="mt-2 text-xs text-gray-600">
                      Días hasta el pago: {calculateDaysUntilPayment()} {calculateDaysUntilPayment() === 1 ? 'día' : 'días'}
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>

          {/* Botones de Acción */}
          <div className="flex justify-end gap-4">
            <button
              type="button"
              onClick={() => navigate('/documents')}
              className="px-6 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50 transition-colors"
            >
              Cancelar
            </button>
            <button
              type="submit"
              disabled={loading}
              className="px-6 py-2 bg-teal-600 text-white rounded-md hover:bg-teal-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {loading ? 'Guardando...' : 'Guardar Documento'}
            </button>
          </div>
        </form>
      </div>

      {/* Modal para crear proveedor */}
      {isCreateSupplierModalOpen && (
        <CreateSupplierModal
          isOpen={isCreateSupplierModalOpen}
          onClose={() => setIsCreateSupplierModalOpen(false)}
          forceProviderOnly={true}
          onSuccess={async (newSupplier) => {
            const response = await supplierService.getSuppliers({ active_only: true, entity_provider: true });
            if (response.success) {
              setSuppliers(response.data);
              setFormData(prev => ({ ...prev, supplier_id: newSupplier.id.toString() }));
            }
            setIsCreateSupplierModalOpen(false);
          }}
        />
      )}

      {/* Toast de notificaciones */}
      <Toast
        isOpen={toast.isOpen}
        message={toast.message}
        type={toast.type}
        onClose={() => setToast({ ...toast, isOpen: false })}
      />
    </div>
  );
};

export default CreateSupplyContinuousDocument;
